import { useState, FormEvent, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);

  // Generate time slots when date changes
  useEffect(() => {
    if (formData.preferredDate) {
      const slots = generateTimeSlots();
      setAvailableSlots(slots);
    } else {
      setAvailableSlots([]);
    }
  }, [formData.preferredDate]);

  const generateTimeSlots = () => {
    const slots: string[] = [];
    const selectedDate = new Date(formData.preferredDate);
    const dayOfWeek = selectedDate.getDay();

    // Business hours based on schedule
    let startHour = 10;
    let endHour = 19;

    if (dayOfWeek === 0) { // Sunday
      startHour = 12;
      endHour = 18;
    } else if (dayOfWeek === 1) { // Monday - Closed
      return [];
    } else if (dayOfWeek === 5) { // Friday
      startHour = 10;
      endHour = 20;
    } else if (dayOfWeek === 6) { // Saturday
      startHour = 11;
      endHour = 19;
    }

    // Generate slots every 30 minutes
    for (let hour = startHour; hour < endHour; hour++) {
      // Add on-the-hour slot
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      // Add half-hour slot
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    // Add final closing hour slot
    slots.push(`${endHour.toString().padStart(2, '0')}:00`);

    return slots;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Save to Supabase
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .insert([
          {
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            preferred_date: formData.preferredDate,
            preferred_time: formData.preferredTime,
            notes: formData.notes,
            status: 'pending'
          }
        ])
        .select()
        .single();

      if (appointmentError) throw appointmentError;

      // Update or create customer record
      const { data: existingCustomer } = await supabase
        .from('customers')
        .select('*')
        .eq('email', formData.email)
        .single();

      if (existingCustomer) {
        await supabase
          .from('customers')
          .update({
            full_name: formData.fullName,
            phone: formData.phone,
            total_visits: existingCustomer.total_visits + 1,
            last_visit: formData.preferredDate,
            updated_at: new Date().toISOString()
          })
          .eq('email', formData.email);
      } else {
        await supabase
          .from('customers')
          .insert([
            {
              full_name: formData.fullName,
              email: formData.email,
              phone: formData.phone,
              total_visits: 1,
              last_visit: formData.preferredDate
            }
          ]);
      }

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        service: '',
        preferredDate: '',
        preferredTime: '',
        notes: ''
      });
      setAvailableSlots([]);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Visit Us</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stop by our shop or reach out - we're here to give you the best grooming experience
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Address</h4>
                <p className="text-gray-600">6111 FM 1960 Rd W<br />Suite 207 E<br />Houston, TX 77069</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                <i className="ri-phone-line text-2xl text-black"></i>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Phone</h4>
                <p className="text-gray-600">(832) 287-5144</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                <i className="ri-mail-line text-2xl text-black"></i>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Email</h4>
                <p className="text-gray-600">Krispykutzinc@gmail.com</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-1">Hours</h4>
                <p className="text-gray-600">
                  Sunday: 12:00 PM - 6:00 PM<br />
                  Monday: Closed<br />
                  Tuesday - Thursday: 10:00 AM - 7:00 PM<br />
                  Friday: 10:00 AM - 8:00 PM<br />
                  Saturday: 11:00 AM - 7:00 PM
                </p>
              </div>
            </div>
          </div>

          {/* Removed the right column with Visit Us, Call Us, and Hours cards */}
        </div>

        <div className="rounded-2xl overflow-hidden shadow-lg h-[600px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3453.8976543210987!2d-95.5789!3d29.9876!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDU5JzE1LjQiTiA5NcKwMzQnNDQuMCJX!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus&q=6111+FM+1960+Rd+W+Suite+207+E+Houston+TX+77069"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Barbershop Location"
          ></iframe>
        </div>
      </div>
    </section>
  );
}
